import { useEffect, useRef, useState } from 'react';

import {
  FONT_OPTIONS,
  TREE_FONT_SIZE_MAX,
  TREE_FONT_SIZE_MIN,
  type TreeAppearance,
} from '../tree/treeAppearance';
import { fontStackToId } from './EditableNoteAppearance';
import {
  APP_WIDTH_MAX,
  APP_WIDTH_MIN,
  APP_WIDTH_STEP,
  GRADIENT_PRESETS,
  isFullWidth,
  resetAllSettingsAndReload,
  useAppearance,
} from '../settings/appearance';
import {
  NOTE_FONT_SIZE_MAX,
  NOTE_FONT_SIZE_MIN,
  NOTE_WIDTH_MAX,
  NOTE_WIDTH_MIN,
  NOTE_WIDTH_STEP,
  isFullNoteWidth,
  useNoteDefaults,
} from '../settings/noteDefaults';
import { useTreeBehaviour } from '../settings/treeBehaviour';

export interface ToggleRailButtonsProps {
  propsVisible: boolean;
  onToggleProps: () => void;
  /**
   * Ship 53: tree font + font-size controls. Owned by VaultLayout
   * via useTreeAppearance(); we just read + dispatch.
   */
  treeAppearance: TreeAppearance;
  /**
   * Ship 70: which subset of buttons this instance renders.
   *   - "toggles" → ℹ️ properties-panel toggle (default; lives next
   *      to search). The 📁 folder-tree toggle that used to share
   *      this slot has been removed — the tree is now always
   *      visible on desktop.
   *   - "settings" → ⚙️ button + popover (lives at the right edge,
   *      before the account menu)
   *
   * Ship 80: variant + onVariantChange dropped from this interface.
   * The Win7/Win11 picker is gone — compact is the only style now.
   * VaultLayout no longer needs to pass them.
   *
   * Default is "toggles" for backwards compatibility with any
   * caller that doesn't pass slot.
   */
  slot?: 'toggles' | 'settings';
}

/**
 * View-control buttons that live in the TopBar:
 *   - slot="toggles":  ℹ️ toggle properties rail
 *   - slot="settings": ⚙️ open settings popover
 *
 * The folder-tree toggle (📁) that used to share the toggles slot
 * has been removed — the tree is now always visible on desktop.
 *
 * The settings popover hosts:
 *   - tree style picker (compact / comfortable)
 *   - tree font + font size (Ship 53)
 *   - note defaults: width / font / font size (Ship 54)
 *   - tree-click behaviour (whole-row vs chevron-only)  ← step 36
 *   - app-width slider (controls the centered .nc-app-frame)
 *   - background gradient grid
 *
 * All settings are global (per-browser, in localStorage).
 *
 * Slider behaviour: while you drag, only the popover's local draft
 * value changes; the actual app frame width is updated on release
 * (pointerup / change / blur). Without that, every onInput tick
 * resizes the topbar, which shifts the cog popover under the
 * cursor — it would feel like the slider is fleeing your mouse.
 * Commit-on-release also makes keyboard arrows feel like discrete
 * steps rather than a continuous resize blur.
 *
 * No "Save" button — every other change applies + persists on the
 * spot, like Notion or VS Code's settings dropdown.
 */
export function ToggleRailButtons({
  propsVisible,
  onToggleProps,
  treeAppearance,
  slot = 'toggles',
}: ToggleRailButtonsProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const { settings, setAppWidth, setGradientId, setTheme } = useAppearance();
  const treeBehaviour = useTreeBehaviour();
  const noteDefaults = useNoteDefaults();

  // Local draft for the slider. While the user drags, this is what
  // the popover label and slider show; the global setting (and the
  // resulting frame resize) lags until release. We keep it in sync
  // with the committed value whenever that changes from elsewhere
  // (cross-tab edit, reset to defaults, etc.).
  const [widthDraft, setWidthDraft] = useState<number>(settings.appWidth);
  useEffect(() => {
    setWidthDraft(settings.appWidth);
  }, [settings.appWidth]);

  // Close picker on outside click/tap. Ship 85: pointerdown not
  // mousedown — see AccountMenu/ContextMenu for rationale (iOS
  // Safari + tap-then-scroll). The "drag on slider thumb doesn't
  // accidentally close" property still holds because pointerdown
  // also fires on mouse left-button-down, which is what the slider
  // listens for; both events bubble up to document, but the
  // pickerRef.contains check excludes inside-popover events
  // identically to before.
  useEffect(() => {
    function onDown(e: PointerEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    }
    if (pickerOpen) {
      document.addEventListener('pointerdown', onDown);
      return () => document.removeEventListener('pointerdown', onDown);
    }
  }, [pickerOpen]);

  /** Commit the current draft width to the global appearance store. */
  function commitWidth() {
    if (widthDraft !== settings.appWidth) {
      setAppWidth(widthDraft);
    }
  }

  // Resolve the current font's id (for the <select>'s value) from
  // its stack. Same helper notes-side appearance uses, so the
  // dropdown shows "Default" for any unrecognised stack.
  const currentTreeFontId = fontStackToId(treeAppearance.fontStack);
  const currentNoteFontId = fontStackToId(noteDefaults.defaults.fontStack);

  // Local drafts for number inputs. We commit on blur / Enter so
  // the user can type "1" → "12" → "16" without each digit being
  // saved as an intermediate value.
  const [treeSizeDraft, setTreeSizeDraft] = useState<string>(
    treeAppearance.fontSize === null ? '' : String(treeAppearance.fontSize),
  );
  useEffect(() => {
    setTreeSizeDraft(
      treeAppearance.fontSize === null ? '' : String(treeAppearance.fontSize),
    );
  }, [treeAppearance.fontSize]);

  // Note width is a slider with the same commit-on-release pattern
  // as the app-width slider above: drag updates only the popover's
  // local draft, and the global setting (which triggers a real
  // editor re-style on every change) only updates on release.
  // Without that, every drag tick reflows the open editor —
  // expensive, and visually distracting.
  const [noteWidthDraft, setNoteWidthDraft] = useState<number>(
    noteDefaults.defaults.width,
  );
  useEffect(() => {
    setNoteWidthDraft(noteDefaults.defaults.width);
  }, [noteDefaults.defaults.width]);

  const [noteSizeDraft, setNoteSizeDraft] = useState<string>(
    String(noteDefaults.defaults.fontSize),
  );
  useEffect(() => {
    setNoteSizeDraft(String(noteDefaults.defaults.fontSize));
  }, [noteDefaults.defaults.fontSize]);

  function commitTreeSize() {
    const trimmed = treeSizeDraft.trim();
    if (trimmed === '') {
      treeAppearance.setFontSize(null);
      return;
    }
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n)) {
      // Reset draft to the persisted value rather than leave it stuck
      // on garbage text.
      setTreeSizeDraft(
        treeAppearance.fontSize === null ? '' : String(treeAppearance.fontSize),
      );
      return;
    }
    const clamped = Math.max(TREE_FONT_SIZE_MIN, Math.min(TREE_FONT_SIZE_MAX, n));
    treeAppearance.setFontSize(clamped);
    if (clamped !== n) {
      setTreeSizeDraft(String(clamped));
    }
  }

  // Note defaults can never be "null / blank" the way the tree's
  // font size can — they always have a numeric value because they
  // ARE the default. The note-width slider's draft is numeric and
  // bounded by the slider min/max, so this only needs to push the
  // current draft to the global setter when it actually changed.
  function commitNoteWidth() {
    if (noteWidthDraft !== noteDefaults.defaults.width) {
      noteDefaults.setWidth(noteWidthDraft);
    }
  }

  function commitNoteSize() {
    const trimmed = noteSizeDraft.trim();
    if (trimmed === '') {
      setNoteSizeDraft(String(noteDefaults.defaults.fontSize));
      return;
    }
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n)) {
      setNoteSizeDraft(String(noteDefaults.defaults.fontSize));
      return;
    }
    const clamped = Math.max(NOTE_FONT_SIZE_MIN, Math.min(NOTE_FONT_SIZE_MAX, n));
    noteDefaults.setFontSize(clamped);
    if (clamped !== n) {
      setNoteSizeDraft(String(clamped));
    }
  }

  // Ship 70: render only the slice the caller asked for. Both
  // slots share the state above (cheap; the only state that
  // matters for "settings" is unused when slot="toggles" but
  // doesn't hurt). Keeping the component unified means the
  // popover content stays in one place — splitting it across
  // two files would mean migrating ~300 lines of useState +
  // commit handlers along with it.
  if (slot === 'settings') {
    return (
      <div ref={pickerRef} className="nc-variant-picker">
        <button
          type="button"
          className="nc-toggle"
          onClick={() => setPickerOpen((v) => !v)}
          title="Settings"
          aria-haspopup="menu"
          aria-expanded={pickerOpen}
        >
          ⚙️
        </button>
        {pickerOpen && (
          <div className="nc-settings-popover" role="menu">
            {/*
              Ship 80: 3-column layout.
                Column 1 (Tree)              — tree font, tree font size, click-to-expand
                Column 2 (Notes)             — note width, note font, note font size
                Column 3 (Layout & theme)    — app width, theme, background

              Pre-Ship-80 the popover was a single tall column that
              triggered a vertical scrollbar on most laptop heights.
              Reorganising into columns keeps everything visible
              within ~700px height.

              The Win7/Win11 tree-style picker is gone. Compact is
              now the only style. The variant prop / onVariantChange
              callback are gone from this component's interface too.
            */}
            <div className="nc-settings-cols">
              {/* ============================================================
                  Column 1: Tree
                  ============================================================ */}
              <div className="nc-settings-col">
                <div className="nc-settings-col-heading">Tree</div>

                <div className="nc-settings-section">
                  <div className="nc-variant-heading">Tree font</div>
                  <select
                    className="nc-settings-select"
                    value={currentTreeFontId}
                    onChange={(e) => {
                      const id = e.currentTarget.value;
                      const opt = FONT_OPTIONS.find((f) => f.id === id);
                      treeAppearance.setFontStack(opt?.stack ?? '');
                    }}
                    aria-label="Tree font"
                  >
                    {FONT_OPTIONS.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="nc-settings-section">
                  <div className="nc-variant-heading">
                    Tree font size
                    <span className="nc-settings-value">
                      {treeAppearance.fontSize === null
                        ? 'Default'
                        : `${treeAppearance.fontSize}px`}
                    </span>
                  </div>
                  <input
                    type="number"
                    className="nc-settings-number"
                    min={TREE_FONT_SIZE_MIN}
                    max={TREE_FONT_SIZE_MAX}
                    step={1}
                    value={treeSizeDraft}
                    placeholder="Default"
                    onChange={(e) => setTreeSizeDraft(e.currentTarget.value)}
                    onBlur={commitTreeSize}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitTreeSize();
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    aria-label="Tree font size in pixels"
                  />
                  <div className="nc-settings-range-labels">
                    <span>{TREE_FONT_SIZE_MIN}px</span>
                    <span>{TREE_FONT_SIZE_MAX}px</span>
                  </div>
                </div>

                <div className="nc-settings-section">
                  <div className="nc-variant-heading">Click to expand folders</div>
                  <button
                    type="button"
                    className={`nc-variant-option ${
                      treeBehaviour.rowClickExpands ? 'nc-variant-option-active' : ''
                    }`}
                    onClick={() => treeBehaviour.setRowClickExpands(true)}
                    role="menuitemradio"
                    aria-checked={treeBehaviour.rowClickExpands}
                    title="Click anywhere on a folder row to expand or collapse it"
                  >
                    Whole row
                  </button>
                  <button
                    type="button"
                    className={`nc-variant-option ${
                      !treeBehaviour.rowClickExpands ? 'nc-variant-option-active' : ''
                    }`}
                    onClick={() => treeBehaviour.setRowClickExpands(false)}
                    role="menuitemradio"
                    aria-checked={!treeBehaviour.rowClickExpands}
                    title="Only the chevron toggles expand. Clicking the row body just selects."
                  >
                    Chevron only
                  </button>
                </div>

                <div className="nc-settings-section">
                  <div className="nc-variant-heading">Collapsing a folder</div>
                  <button
                    type="button"
                    className={`nc-variant-option ${
                      treeBehaviour.collapseChildren ? 'nc-variant-option-active' : ''
                    }`}
                    onClick={() => treeBehaviour.setCollapseChildren(true)}
                    role="menuitemradio"
                    aria-checked={treeBehaviour.collapseChildren}
                    title="Collapsing a folder also collapses everything inside it. Re-opening shows it collapsed."
                  >
                    Collapse subtree
                  </button>
                  <button
                    type="button"
                    className={`nc-variant-option ${
                      !treeBehaviour.collapseChildren ? 'nc-variant-option-active' : ''
                    }`}
                    onClick={() => treeBehaviour.setCollapseChildren(false)}
                    role="menuitemradio"
                    aria-checked={!treeBehaviour.collapseChildren}
                    title="Collapsing a folder keeps its sub-folders' expanded state, so they reappear when you re-open it."
                  >
                    Keep open
                  </button>
                </div>
              </div>

              {/* ============================================================
                  Column 2: Notes
                  ============================================================ */}
              <div className="nc-settings-col">
                <div className="nc-settings-col-heading">Notes</div>

                <div className="nc-settings-section">
                  <div className="nc-variant-heading">
                    Note width
                    <span className="nc-settings-value">
                      {isFullNoteWidth(noteWidthDraft)
                        ? 'Full width'
                        : `${noteWidthDraft}px`}
                    </span>
                  </div>
                  <input
                    type="range"
                    className="nc-settings-slider"
                    min={NOTE_WIDTH_MIN}
                    max={NOTE_WIDTH_MAX}
                    step={NOTE_WIDTH_STEP}
                    value={noteWidthDraft}
                    onChange={(e) =>
                      setNoteWidthDraft(parseInt(e.currentTarget.value, 10))
                    }
                    onMouseUp={commitNoteWidth}
                    onTouchEnd={commitNoteWidth}
                    onPointerUp={commitNoteWidth}
                    onKeyUp={commitNoteWidth}
                    onBlur={commitNoteWidth}
                    aria-label="Default note width"
                    aria-valuetext={
                      isFullNoteWidth(noteWidthDraft)
                        ? 'Full width'
                        : `${noteWidthDraft} pixels`
                    }
                  />
                  <div className="nc-settings-range-labels">
                    <span>{NOTE_WIDTH_MIN}px</span>
                    <span>Full</span>
                  </div>
                </div>

                <div className="nc-settings-section">
                  <div className="nc-variant-heading">Note font</div>
                  <select
                    className="nc-settings-select"
                    value={currentNoteFontId}
                    onChange={(e) => {
                      const id = e.currentTarget.value;
                      const opt = FONT_OPTIONS.find((f) => f.id === id);
                      noteDefaults.setFontStack(opt?.stack ?? '');
                    }}
                    aria-label="Default note font"
                  >
                    {FONT_OPTIONS.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="nc-settings-section">
                  <div className="nc-variant-heading">
                    Note font size
                    <span className="nc-settings-value">
                      {noteDefaults.defaults.fontSize}px
                    </span>
                  </div>
                  <input
                    type="number"
                    className="nc-settings-number"
                    min={NOTE_FONT_SIZE_MIN}
                    max={NOTE_FONT_SIZE_MAX}
                    step={1}
                    value={noteSizeDraft}
                    onChange={(e) => setNoteSizeDraft(e.currentTarget.value)}
                    onBlur={commitNoteSize}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitNoteSize();
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    aria-label="Default note font size in pixels"
                  />
                  <div className="nc-settings-range-labels">
                    <span>{NOTE_FONT_SIZE_MIN}px</span>
                    <span>{NOTE_FONT_SIZE_MAX}px</span>
                  </div>
                </div>
              </div>

              {/* ============================================================
                  Column 3: Layout & theme
                  ============================================================ */}
              <div className="nc-settings-col">
                <div className="nc-settings-col-heading">Layout &amp; theme</div>

                <div className="nc-settings-section">
                  <div className="nc-variant-heading">
                    App width
                    <span className="nc-settings-value">
                      {isFullWidth(widthDraft) ? 'Full width' : `${widthDraft}px`}
                    </span>
                  </div>
                  <input
                    type="range"
                    className="nc-settings-slider"
                    min={APP_WIDTH_MIN}
                    max={APP_WIDTH_MAX}
                    step={APP_WIDTH_STEP}
                    value={widthDraft}
                    onChange={(e) =>
                      setWidthDraft(parseInt(e.currentTarget.value, 10))
                    }
                    onMouseUp={commitWidth}
                    onTouchEnd={commitWidth}
                    onPointerUp={commitWidth}
                    onKeyUp={commitWidth}
                    onBlur={commitWidth}
                    aria-label="App frame width"
                    aria-valuetext={
                      isFullWidth(widthDraft) ? 'Full width' : `${widthDraft} pixels`
                    }
                  />
                  <div className="nc-settings-range-labels">
                    <span>{APP_WIDTH_MIN}px</span>
                    <span>Full</span>
                  </div>
                </div>

                {/*
                  Ship 80: theme picker. 'Auto' = honour OS via
                  prefers-color-scheme; 'Light' / 'Dark' force a
                  fixed scheme via [data-theme] override on <html>.
                */}
                <div className="nc-settings-section">
                  <div className="nc-variant-heading">Theme</div>
                  <button
                    type="button"
                    className={`nc-variant-option ${
                      settings.theme === 'auto' ? 'nc-variant-option-active' : ''
                    }`}
                    onClick={() => setTheme('auto')}
                    role="menuitemradio"
                    aria-checked={settings.theme === 'auto'}
                    title="Follow the operating system's light/dark setting"
                  >
                    Auto
                  </button>
                  <button
                    type="button"
                    className={`nc-variant-option ${
                      settings.theme === 'light' ? 'nc-variant-option-active' : ''
                    }`}
                    onClick={() => setTheme('light')}
                    role="menuitemradio"
                    aria-checked={settings.theme === 'light'}
                  >
                    Light
                  </button>
                  <button
                    type="button"
                    className={`nc-variant-option ${
                      settings.theme === 'dark' ? 'nc-variant-option-active' : ''
                    }`}
                    onClick={() => setTheme('dark')}
                    role="menuitemradio"
                    aria-checked={settings.theme === 'dark'}
                  >
                    Dark
                  </button>
                </div>

                <div className="nc-settings-section">
                  <div className="nc-variant-heading">Background</div>
                  <div className="nc-settings-gradient-grid">
                    {GRADIENT_PRESETS.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        className={`nc-settings-gradient-swatch ${
                          g.id === settings.gradientId
                            ? 'nc-settings-gradient-swatch-active'
                            : ''
                        }`}
                        onClick={() => setGradientId(g.id)}
                        title={g.label}
                        aria-label={`Background: ${g.label}`}
                        aria-pressed={g.id === settings.gradientId}
                      >
                        <span
                          className="nc-settings-gradient-swatch-fill"
                          style={{ background: g.light }}
                        />
                        <span className="nc-settings-gradient-swatch-label">
                          {g.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/*
              Ship 80: Reset row spans the full popover width below
              the columns. Confirm dialog before wiping — destructive
              and undo isn't possible since values are gone from
              localStorage. Reload after wipe so all consumers
              re-initialise from defaults.
            */}
            <div className="nc-settings-divider" />
            <div className="nc-settings-section nc-settings-reset-row">
              <button
                type="button"
                className="nc-settings-reset-btn"
                onClick={() => {
                  if (
                    window.confirm(
                      'Reset all settings (theme, fonts, widths, layout, background) to defaults? This will reload the page.',
                    )
                  ) {
                    resetAllSettingsAndReload();
                  }
                }}
                title="Wipe all local settings and reload"
              >
                ↺ Reset all settings to defaults
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default slot: ℹ️ properties-panel toggle only. The folder-tree
  // toggle (📁) that used to live next to it has been removed — the
  // tree is now always visible on desktop, so a toggle for it would
  // do nothing useful. The .nc-toggles wrapper stays as a single-
  // child flex container so future buttons can be added back here
  // without touching layout CSS.
  return (
    <div className="nc-toggles">
      <button
        type="button"
        className={`nc-toggle ${propsVisible ? 'nc-toggle-on' : ''}`}
        onClick={onToggleProps}
        title={propsVisible ? 'Hide properties panel' : 'Show properties panel'}
        aria-pressed={propsVisible}
      >
        ℹ️
      </button>
    </div>
  );
}
